import { useState, useEffect } from 'react';
import { Box, Divider } from '@mui/material';
import { SellerPlan } from '../../../../Models/SellerPlan';
import { PlanDetailsInterface } from '../../../../Models/Seller';
import { SellerAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/SellerAPIhandler';
import { ErrorLogger } from '../../../../utils/Logger';
import {
    LoadingState,
    ErrorState,
    EmptyState,
    PlanHeader,
    PriceExpiryCard,
    MetricsGrid,
    FeaturesSection,
    TermsSection
} from './components';

interface SellerPlanDetailsModalProps {
    planDetails: PlanDetailsInterface;
}

export const SellerPlanDetailsModal = ({ planDetails }: SellerPlanDetailsModalProps) => {
    const [plan, setPlan] = useState<SellerPlan | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPlanDetails = async () => {
            if (!planDetails?.plan) return;

            setLoading(true);
            setError(null);

            try {
                // Get the plan ID - it could be a string (ID) or populated object
                const planId = typeof planDetails.plan === 'string'
                    ? planDetails.plan
                    : planDetails.plan._id || planDetails.plan.id;

                const fetchedPlan = await SellerAPIHandler.shared.getSellerPlanById(planId);
                setPlan(fetchedPlan);
            } catch (err) {
                ErrorLogger.log(err, 'SellerPlanDetailsModal.fetchPlanDetails');
                setError("Failed to load plan details. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchPlanDetails();
    }, [planDetails]);

    const isPlanActive = new Date(planDetails.expiryTimeStamp) >= new Date();

    if (loading) return <LoadingState />;
    if (error) return <ErrorState message={error} />;
    if (!plan) return <EmptyState />;

    return (
        <Box sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '24px 28px' }}>
            <PlanHeader 
                name={plan.name}
                description={plan.description}
                isPlanActive={isPlanActive}
            />

            <PriceExpiryCard
                price={plan.price}
                billingCycle={plan.billingCycle}
                expiryTimeStamp={planDetails.expiryTimeStamp}
                isPlanActive={isPlanActive}
            />

            <Divider sx={{ marginY: 3, borderColor: '#e2e8f0' }} />

            <MetricsGrid
                platformFees={plan.sellerPlatformFees}
                maxGigsAllowed={plan.maxGigsAllowed}
                maxOrdersPerMonth={plan.maxOrdersPerMonth}
                prioritySupport={plan.prioritySupport}
            />

            {plan.features && plan.features.length > 0 && (
                <>
                    <Divider sx={{ marginY: 3, borderColor: '#e2e8f0' }} />
                    <FeaturesSection features={plan.features} />
                </>
            )}

            {plan.termsAndConditions && plan.termsAndConditions.length > 0 && (
                <>
                    <Divider sx={{ marginY: 3, borderColor: '#e2e8f0' }} />
                    <TermsSection terms={plan.termsAndConditions} />
                </>
            )}
        </Box>
    );
};
